import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity, Stethoscope, Baby, FlaskConical, HeartPulse,
  Sparkles, Leaf, ShieldCheck, MessageCircle, CalendarHeart,
} from "lucide-react";
import PerlaCard from "../components/PerlaCard";
import WhatsAppCTABlock from "../components/WhatsAppCTABlock";
import { SITE, waLink } from "../lib/site";
import { ShieldPlus } from "lucide-react";

const includes = [
  { icon: Activity, t: "Ultrasonido obstétrico", d: "Evaluación visual del crecimiento y bienestar fetal." },
  { icon: Stethoscope, t: "Seguimiento del embarazo", d: "Consultas periódicas y orientación en cada trimestre." },
  { icon: HeartPulse, t: "Valoración materna", d: "Cuidado integral de tu salud durante el embarazo." },
  { icon: Baby, t: "Valoración fetal", d: "Monitoreo del desarrollo y signos vitales del bebé." },
  { icon: FlaskConical, t: "Estudios complementarios", d: "Laboratorios y pruebas indicadas en cada etapa." },
];

const perlas = [
  { icon: Leaf, text: "El ácido fólico debe iniciarse idealmente antes del embarazo." },
  { icon: HeartPulse, text: "Los movimientos fetales son un indicador importante del bienestar fetal." },
  { icon: ShieldCheck, text: "El control prenatal oportuno ayuda a prevenir complicaciones." },
];

const timeline = [
  { t: "Trimestre 1", w: "Semanas 1 – 13", d: "Confirmación, ácido fólico, perfil inicial y primer ultrasonido." },
  { t: "Trimestre 2", w: "Semanas 14 – 27", d: "Anatomía fetal, glucosa, control de peso y presión arterial." },
  { t: "Trimestre 3", w: "Semanas 28 – 40", d: "Bienestar fetal, preparación al parto y plan de nacimiento." },
];

export default function ControlPrenatal() {
  return (
    <div data-testid="page-control-prenatal">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-snow via-brand-cloud/50 to-white" />
        <div className="absolute top-20 left-20 w-80 h-80 rounded-full bg-brand-bright/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-brand-medical mb-4">Control Prenatal</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-deep leading-[1.05]">
              Acompañarte en el <em className="not-italic text-gradient-blue">momento más esperado</em>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-brand-deep/70 font-light leading-relaxed max-w-xl">
              Un seguimiento cuidadoso, cálido y profesional desde antes de la concepción
              hasta el nacimiento de tu bebé.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="prenatal-agendar" className="inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-7 py-3.5 font-medium tracking-wide transition-all premium-shadow">
                <CalendarHeart className="w-4 h-4" /> Agenda tu control prenatal
              </a>
              <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="prenatal-whatsapp" className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-medical/40 text-brand-medical hover:bg-white px-7 py-3.5 font-medium tracking-wide transition-all">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-6 relative"
          >
            <div className="absolute -inset-3 rounded-[3rem] bg-gradient-to-br from-brand-bright/30 to-brand-silver/30 blur-2xl" />
            <div className="relative rounded-[2.5rem] overflow-hidden hairline premium-shadow aspect-[5/4]">
              <img src={SITE.images.prenatal} alt="Mamá embarazada" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* QUÉ INCLUYE */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Qué incluye</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
              Un control prenatal <em className="not-italic text-gradient-blue">integral</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end text-brand-deep/70 font-light text-lg leading-relaxed">
            Cada cita está pensada para acompañarte con información clara, tiempo
            suficiente y la tranquilidad de un seguimiento personalizado.
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {includes.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`prenatal-incluye-${i}`}
              className="bg-white rounded-3xl p-7 hairline premium-shadow hover:-translate-y-1 transition-transform duration-500"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-cloud to-brand-silver ring-1 ring-brand-silver/60 flex items-center justify-center mb-5">
                <s.icon className="w-5 h-5 text-brand-medical" strokeWidth={1.4} />
              </div>
              <h3 className="font-serif text-2xl text-brand-deep mb-2">{s.t}</h3>
              <p className="text-brand-deep/70 font-light leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-cloud/40 to-white" />
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-14 text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Línea del tiempo</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep">
              Los <em className="not-italic text-gradient-blue">9 meses</em>, paso a paso.
            </h2>
          </div>

          <div className="relative grid md:grid-cols-3 gap-6">
            <div className="hidden md:block absolute top-12 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-brand-bright to-transparent" />
            {timeline.map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                data-testid={`prenatal-timeline-${i}`}
                className="relative bg-white rounded-3xl p-8 hairline premium-shadow"
              >
                <div className="absolute -top-5 left-8 w-10 h-10 rounded-full bg-gradient-to-br from-brand-bright to-brand-medical text-white flex items-center justify-center font-serif text-lg ring-4 ring-white">
                  {i + 1}
                </div>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mt-3 mb-1">{s.w}</p>
                <h3 className="font-serif text-2xl text-brand-deep mb-3">{s.t}</h3>
                <p className="text-brand-deep/70 font-light leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CALIDAD Y SEGURIDAD */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="prenatal-quality-block">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-deep via-brand-medical to-brand-bright" />
        <div className="absolute -top-32 -left-20 w-[460px] h-[460px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-20 w-[460px] h-[460px] rounded-full bg-brand-bright/30 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 ring-1 ring-white/20 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-white mb-5">
              <ShieldPlus className="w-3.5 h-3.5" strokeWidth={1.6} /> Calidad · Seguridad · Estructura
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-[1.05] mb-5">
              Control prenatal con enfoque en <em className="not-italic text-brand-silver">calidad y seguridad</em>.
            </h2>
            <p className="text-white/85 font-light text-lg leading-relaxed max-w-2xl">
              Mi formación en <strong className="font-medium text-white">Administración Hospitalaria</strong> me
              permite implementar un control prenatal estructurado, con seguimiento
              puntual de estudios, ultrasonidos y factores de riesgo, ofreciendo
              mayor seguridad tanto para la madre como para el bebé.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl bg-white/10 backdrop-blur-md ring-1 ring-white/15 p-6 space-y-4">
              {[
                { k: "Estudios", v: "Calendarización por trimestre" },
                { k: "Ultrasonidos", v: "Seguimiento del bienestar fetal" },
                { k: "Factores de riesgo", v: "Detección temprana y manejo" },
                { k: "Bitácora clínica", v: "Historia documentada y trazable" },
              ].map((it) => (
                <div key={it.k} className="flex items-center justify-between gap-4">
                  <span className="text-sm uppercase tracking-[0.18em] text-brand-silver">{it.k}</span>
                  <span className="font-serif text-white text-right leading-tight">{it.v}</span>
                </div>
              ))}
              <div className="h-px bg-white/15" />
              <p className="text-xs text-white/65 leading-relaxed">
                Procesos inspirados en estándares de hospital privado, aplicados a tu consulta personalizada.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ULTRASOUND GALLERY */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-brand-snow to-brand-cloud/40" />
        <div className="absolute top-20 right-10 w-80 h-80 rounded-full bg-brand-bright/10 blur-3xl pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Tecnología y precisión</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
                Imágenes que <em className="not-italic text-gradient-blue">cuentan historias</em>.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end text-brand-deep/70 font-light text-lg leading-relaxed">
              Ultrasonidos obstétricos de alta resolución para acompañar el desarrollo
              de tu bebé en cada etapa del embarazo.
            </div>
          </div>

          {/* Masonry-style grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { src: "https://images.pexels.com/photos/7089623/pexels-photo-7089623.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Ultrasonido obstétrico mostrando feto en monitor", tall: true, label: "Visualización fetal" },
              { src: "https://images.pexels.com/photos/11837653/pexels-photo-11837653.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Imagen detallada de sonograma de embarazo", label: "Detalle del sonograma" },
              { src: "https://images.pexels.com/photos/7088828/pexels-photo-7088828.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Equipo de ultrasonido moderno", label: "Equipo moderno" },
              { src: "https://images.pexels.com/photos/7108420/pexels-photo-7108420.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Doctor realizando ultrasonido a paciente embarazada", tall: true, label: "Acompañamiento médico" },
              { src: "https://images.pexels.com/photos/7088539/pexels-photo-7088539.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Impresión 3D del ultrasonido y equipo médico", label: "Resultados impresos" },
              { src: "https://images.pexels.com/photos/7108421/pexels-photo-7108421.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Pantalla del equipo de ultrasonido", label: "Imagen en tiempo real" },
            ].map((img, i) => (
              <motion.figure
                key={img.src}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.65, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`ultrasound-${i}`}
                className={`group relative rounded-3xl overflow-hidden hairline premium-shadow bg-white ${
                  img.tall ? "row-span-2 aspect-[3/5]" : "aspect-square"
                }`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/85 via-brand-deep/10 to-transparent opacity-90" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-brand-silver mb-1">Ultrasonido</p>
                  <p className="font-serif text-lg md:text-xl text-white leading-tight">{img.label}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          <p className="mt-6 text-xs text-brand-deep/45 font-light text-center">
            Imágenes ilustrativas. Cada estudio se interpreta de forma personalizada en consulta.
          </p>
        </div>
      </section>

      {/* PERLAS */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-28">
        <div className="mb-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Perlas de salud</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
              Pequeños consejos, <em className="not-italic text-gradient-blue">grandes diferencias</em>.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {perlas.map((p, i) => (
            <PerlaCard key={i} icon={p.icon} text={p.text} index={i} testid={`prenatal-perla-${i}`} title />
          ))}
        </div>
      </section>

      {/* WhatsApp CTAs */}
      <WhatsAppCTABlock highlight="cta-prenatal" />
    </div>
  );
}
